import { useStyletron } from '@thanx/uikit/theme'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { AppLandingPageTheme } from 'slices/appLandingPageTheme/types'
import { merchantTheme } from 'theme'
import BlockItem from './BlockItem'

type ExpiredBlockProps = {
  expiredBlocks: AppLandingPageTheme['contentBlocks']
  onEdit: (id: string) => void
  onRemove: (id: string) => void
}

const t = buildTranslate('cms.content.app_home_page.content.expired_blocks')

const ExpiredBlocks = ({
  expiredBlocks,
  onEdit,
  onRemove,
}: ExpiredBlockProps) => {
  const [css] = useStyletron()
  const [isExpanded, setIsExpanded] = useState(false)
  const expiredBlockIds = Object.keys(expiredBlocks)

  if (expiredBlockIds.length === 0) {
    return null
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const getExpiredBlocksText = () => {
    const count = expiredBlockIds.length
    const key =
      count === 1 ? 'expired_content_blocks_1' : 'expired_content_blocks'
    return t(key, { count })
  }

  // Sort expired blocks by end_at date in descending order (most recently expired first)
  const sortedExpiredBlockIds = expiredBlockIds.sort((a, b) => {
    const blockA = expiredBlocks[a]
    const blockB = expiredBlocks[b]

    // Handle blocks without end_at (shouldn't happen for expired blocks, but just in case)
    if (!blockA?.end_at || !blockB?.end_at) {
      return 0
    }

    // Sort by end_at date descending (most recent first)
    return new Date(blockB.end_at).getTime() - new Date(blockA.end_at).getTime()
  })

  return (
    <div style={{ marginTop: '0', marginBottom: '24px' }}>
      {/* Collapsible Header */}
      <div
        className={css({
          display: 'flex',
          alignItems: 'start',
          gap: '8px',
          marginBottom: isExpanded ? '24px' : '0',
          cursor: 'pointer',
        })}
        onClick={toggleExpanded}
      >
        <span
          className={css({
            fontFamily: 'Lato',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0.25px',
            color: merchantTheme.colors.grey70,
          })}
        >
          {getExpiredBlocksText()}
        </span>
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
          })}
        >
          <span
            className={css({
              fontFamily: 'Lato',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: merchantTheme.colors.belizeHole50,
            })}
          >
            {t(isExpanded ? 'collapse' : 'expand')}
          </span>
          <i
            className={`fa fa-chevron-down fa-lg st-bv ${css({
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              color: merchantTheme.colors.belizeHole50,
              marginLeft: '4px',
            })}`}
          />
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div>
          {sortedExpiredBlockIds.map((blockId, index) => {
            const block = expiredBlocks[blockId]
            if (!block) return null

            return (
              <div
                key={blockId}
                style={{
                  marginBottom: '16px',
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <BlockItem
                  id={blockId}
                  index={index}
                  data={block}
                  onEdit={onEdit}
                  onRemove={onRemove}
                  expired={true}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ExpiredBlocks
